import { useState } from "react"
import { useQueryState } from "nuqs"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSearch, faTimes } from "@fortawesome/free-solid-svg-icons"
import ApiError from "../../components/ApiError"
import Loader from "../../components/Loader"
import DateRangeControl from "../../components/DateRangeControl"
import Dropdown from "../../components/Dropdown"
import VideoLinkPreview from "../../components/VideoLinkPreview"
import { useVideoAnalytics } from "../../hooks/useVideoLogs"
import {
  useDateRangeFilter,
  CUSTOM_RANGE,
} from "../../hooks/useDateRangeFilter"
import { sourceOptions } from "../../constants"
import { formatDuration } from "../../utils/datetime"
import { getViewMedicaShareUrl } from "../../utils/viewMediaPlayer"

const DEFAULT_RANGE = "This Month"
const ALL_SOURCES = "All"
const TOP_COUNT = 10
const EMPTY_VALUE = "—"

const formatPlays = (plays) =>
  `${plays.toLocaleString()} ${plays === 1 ? "time" : "times"}`

const TableHeaderCell = ({ children, align = "text-left" }) => (
  <th
    className={`sticky top-0 bg-white px-3 py-2 ${align} text-[10px] font-semibold uppercase tracking-wider text-gray-500`}
  >
    {children}
  </th>
)

const TableCell = ({ children, align = "text-left", className = "" }) => (
  <td className={`px-3 py-2.5 text-sm ${align} ${className}`}>{children}</td>
)

const Card = ({ title, subtitle, action, isEmpty, emptyLabel, children }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-baseline gap-2">
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        {subtitle && <span className="text-xs text-gray-400">{subtitle}</span>}
      </div>
      {action}
    </div>
    {isEmpty ? (
      <div className="flex h-32 items-center justify-center text-sm text-gray-400">
        {emptyLabel}
      </div>
    ) : (
      children
    )}
  </div>
)

/*
  Both lists use this table, and their columns have to line up. So the layout
  is fixed (the same widths whatever the rows hold) and both reserve the
  scrollbar's gutter, which only the library's scroll area would otherwise
  take out of its width.
*/
const VideoTable = ({ videos, className = "" }) => (
  <div className={`overflow-auto [scrollbar-gutter:stable] ${className}`}>
    <table className="w-full table-fixed divide-y divide-gray-100">
      <colgroup>
        <col className="w-12" />
        <col />
        <col className="w-28" />
        <col className="w-28" />
        <col className="w-36" />
      </colgroup>
      <thead>
        <tr>
          <TableHeaderCell>#</TableHeaderCell>
          <TableHeaderCell>Video</TableHeaderCell>
          <TableHeaderCell align="text-right">Duration</TableHeaderCell>
          <TableHeaderCell align="text-right">Plays</TableHeaderCell>
          <TableHeaderCell align="text-right">Avg % Watched</TableHeaderCell>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-50">
        {videos.map((video) => (
          <tr
            key={video.videoId}
            className="transition-colors hover:bg-gray-50"
          >
            <TableCell className="tabular-nums text-gray-400">
              {video.rank}
            </TableCell>
            <TableCell className="font-medium text-gray-900">
              {/* Hovering the name previews the video, same as the event
                  videos list; a video with no stored link is plain text. */}
              {video.videoLink ? (
                <VideoLinkPreview
                  className="hover:text-blue-600 hover:underline"
                  link={video.videoLink}
                  openLink={getViewMedicaShareUrl(video.videoLink)}
                  title={video.title}
                  videoId={video.videoId}
                >
                  {video.title}
                </VideoLinkPreview>
              ) : (
                video.title
              )}
            </TableCell>
            <TableCell
              align="text-right"
              className="tabular-nums text-gray-500"
            >
              {formatDuration(video.durationSeconds) ?? EMPTY_VALUE}
            </TableCell>
            <TableCell
              align="text-right"
              className="tabular-nums text-gray-700"
            >
              {formatPlays(video.plays)}
            </TableCell>
            <TableCell
              align="text-right"
              className="tabular-nums text-gray-700"
            >
              {video.avgPercentWatched === null
                ? EMPTY_VALUE
                : `${video.avgPercentWatched}%`}
            </TableCell>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

const SearchInput = ({ value, onChange }) => (
  <div className="relative">
    <FontAwesomeIcon
      className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
      icon={faSearch}
    />
    <input
      aria-label="Search videos by name"
      className="w-full min-w-[260px] rounded-md border border-gray-300 py-1.5 pl-9 pr-8 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
      placeholder="Search by video name"
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
    {value && (
      <button
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        title="Clear search"
        type="button"
        onClick={() => onChange("")}
      >
        <FontAwesomeIcon className="h-3.5 w-3.5" icon={faTimes} />
      </button>
    )}
  </div>
)

const VideoAnalytics = () => {
  const [librarySearch, setLibrarySearch] = useState("")
  const {
    dateRange,
    start,
    end,
    triggerLabel,
    presetOptions,
    getCustomSeed,
    handleSelectPreset,
    handleApplyCustom,
    handleResetFilters: handleResetDateRange,
  } = useDateRangeFilter({ defaultRange: DEFAULT_RANGE, includeAllTime: true })
  const timePeriod = start || end ? [start, end].join(",") : undefined
  const [source, setSource] = useQueryState("source", {
    defaultValue: ALL_SOURCES,
  })

  const handleResetFilters = () => {
    handleResetDateRange()
    setSource(ALL_SOURCES)
  }

  const {
    data: videos = [],
    isLoading,
    isError,
    error,
  } = useVideoAnalytics({
    timePeriod,
    source: source === ALL_SOURCES ? undefined : source,
  })

  if (isError) {
    return (
      <div className="mx-auto mt-4 max-w-[1400px] px-4">
        <ApiError axiosError={error} />
      </div>
    )
  }

  // The API returns videos most-played first, so the top 10 is the head of
  // the list — minus videos nobody played, which aren't "watched".
  // Ranked once over the whole list, so a video keeps its rank in the search
  // results instead of being renumbered.
  const rankedVideos = videos.map((video, index) => ({
    ...video,
    rank: index + 1,
  }))
  const topVideos = rankedVideos
    .filter((video) => video.plays > 0)
    .slice(0, TOP_COUNT)
  // The whole library is already loaded, so the search filters it in place
  // as the user types rather than going back to the API.
  const searchTerm = librarySearch.trim().toLowerCase()
  const libraryVideos = searchTerm
    ? rankedVideos.filter((video) =>
        video.title?.toLowerCase().includes(searchTerm)
      )
    : rankedVideos
  const hasActiveFilters = dateRange !== DEFAULT_RANGE || source !== ALL_SOURCES

  return (
    <div className="mx-auto max-w-[90%] px-4 pb-6 pt-2 sm:px-6">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <h1 className="text-2xl font-extrabold text-primary">
          Video Analytics
        </h1>

        <div className="flex items-end gap-2">
          <Dropdown
            label="SOURCE"
            options={[ALL_SOURCES, ...sourceOptions]}
            setValue={setSource}
            title="Filter by report source"
            value={source}
          />
          <DateRangeControl
            customLabel={CUSTOM_RANGE}
            getCustomSeed={getCustomSeed}
            presets={presetOptions}
            triggerLabel={triggerLabel}
            value={dateRange}
            onApplyCustom={handleApplyCustom}
            onSelectPreset={handleSelectPreset}
          />
          <button
            className="rounded-lg border border-gray-300 px-3.5 py-2 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            disabled={!hasActiveFilters}
            title="Reset to This Month and all sources"
            type="button"
            onClick={handleResetFilters}
          >
            Reset
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="py-16">
          <Loader />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <Card
            emptyLabel="No videos were watched for these filters."
            isEmpty={!topVideos.length}
            title="Top 10 Videos Watched"
          >
            <VideoTable videos={topVideos} />
          </Card>

          <Card
            action={
              <SearchInput value={librarySearch} onChange={setLibrarySearch} />
            }
            emptyLabel={
              searchTerm
                ? `No videos match "${librarySearch.trim()}".`
                : "No videos in the library."
            }
            isEmpty={!libraryVideos.length}
            subtitle={
              searchTerm
                ? `${libraryVideos.length.toLocaleString()} of ${videos.length.toLocaleString()} videos`
                : `${videos.length.toLocaleString()} videos`
            }
            title="Video Library"
          >
            <VideoTable className="max-h-[600px]" videos={libraryVideos} />
          </Card>
        </div>
      )}
    </div>
  )
}

export default VideoAnalytics
